import type { ListNode } from '~/client/src/shared/lists/core'

import { CompanyStatus, CompanyStatusAction, ICompanyRow } from './types'

export enum MergeAvailability {
  Hidden = 'hidden',
  Disabled = 'disabled',
  Enabled = 'enabled',
}

export interface CompanySelectionActions {
  readonly statusAction: CompanyStatusAction | null
  readonly merge: MergeAvailability
}

const MIN_COMPANIES_TO_MERGE = 2

function getStatusAction(
  selected: readonly Pick<ICompanyRow, 'status'>[],
): CompanyStatusAction | null {
  if (!selected.length) return null
  if (selected.every(row => row.status === CompanyStatus.Active)) {
    return CompanyStatusAction.Deactivate
  }
  if (selected.every(row => row.status === CompanyStatus.Deactivated)) {
    return CompanyStatusAction.Activate
  }
  return null
}

function getMergeAvailability(
  selectedCount: number,
  isSTXAdmin: boolean,
): MergeAvailability {
  if (!isSTXAdmin) return MergeAvailability.Hidden
  return selectedCount >= MIN_COMPANIES_TO_MERGE
    ? MergeAvailability.Enabled
    : MergeAvailability.Disabled
}

export function getCompanySelectionActions(
  selected: readonly Pick<ICompanyRow, 'status'>[],
  isSTXAdmin: boolean,
): CompanySelectionActions {
  return {
    statusAction: getStatusAction(selected),
    merge: getMergeAvailability(selected.length, isSTXAdmin),
  }
}

export function getMergeCompanyIds(
  selected: readonly Pick<ICompanyRow, 'id'>[],
  nodes: readonly ListNode<unknown>[],
): string[] {
  const positions = new Map<string, number>()
  nodes.forEach((node, index) => {
    if (node.kind === 'record' && !positions.has(node.recordId)) {
      positions.set(node.recordId, index)
    }
  })
  const positionOf = (id: string) => positions.get(id) ?? nodes.length

  return selected
    .map(row => row.id)
    .sort((a, b) => positionOf(a) - positionOf(b))
}
